import { Network } from '@capacitor/network';
import { useState, useEffect } from 'react';

export function useNetworkStatus() {
  // Initialize to false instead of navigator.onLine: WKWebView's navigator.onLine is
  // unreliable and may report true even when the device is offline. We only set
  // isOnline=true after the first Network.getStatus() resolves to prevent ChatPage
  // from running retryPendingMessages against a down network before status is known.
  const [isOnline, setIsOnline] = useState(false);
  // isInitialized becomes true once the first real status check (native or fallback)
  // has completed. ChatPage uses this to gate the mount-time retry so it never fires
  // before the authoritative network state is known.
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    let capacitorHandle: { remove: () => void } | null = null;

    const setupCapacitorListener = async () => {
      try {
        // Get initial status from native plugin (more reliable than navigator.onLine)
        const status = await Network.getStatus();
        if (!mounted) return;
        setIsOnline(status.connected);
        setIsInitialized(true);

        // Subscribe to changes via native plugin
        const handle = await Network.addListener('networkStatusChange', (s) => {
          setIsOnline(s.connected);
        });
        if (!mounted) {
          handle.remove();
          return;
        }
        capacitorHandle = handle;
      } catch {
        // Native plugin not available (OTA update on old binary) — fall back to browser events.
        // Check mounted before attaching: if the component unmounted while awaiting above,
        // the cleanup already ran and we must not attach handlers that can never be removed.
        if (!mounted) return;
        const onOnline = () => setIsOnline(true);
        const onOffline = () => setIsOnline(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        // Mark initialized with the current browser value; events will keep it in sync.
        setIsOnline(navigator.onLine);
        setIsInitialized(true);
        capacitorHandle = {
          remove: () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
          },
        };
      }
    };

    setupCapacitorListener();

    return () => {
      mounted = false;
      capacitorHandle?.remove();
    };
  }, []);

  return { isOnline, isInitialized };
}
